import React, { useMemo, useState } from 'react';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const AVAILABLE_METRICS = [
  { id: 'totalRecords', label: 'Total Records', icon: '📋', defaultVisible: true },
  { id: 'uniqueTrades', label: 'Unique Trades', icon: '👷', defaultVisible: true },
  { id: 'avgStraight', label: 'Avg Straight', icon: '💵', defaultVisible: true },
  { id: 'avgOvertime', label: 'Avg Overtime', icon: '⏱️', defaultVisible: true },
];

const AVAILABLE_CHARTS = [
  { id: 'byTrade', label: 'By Trade', icon: '👷', defaultVisible: true, defaultType: 'bar' },
  { id: 'byWorkSchedule', label: 'By Work Schedule', icon: '📅', defaultVisible: true, defaultType: 'bar' },
  { id: 'rateDistribution', label: 'Rate Distribution', icon: '📊', defaultVisible: true, defaultType: 'bar' },
];

/**
 * LabourRatesAnalyticsDashboard - Analytics Dashboard for Labour Rates
 * Displays rate comparisons, trade distributions, and cost projections
 */
const LabourRatesAnalyticsDashboard = ({ data, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('labourRatesAnalytics', 'global', true);
  
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
  } = useAnalyticsSettings('labour_rates_log', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalRecords: 0,
        uniqueTrades: 0,
        avgStraight: 0,
        avgOvertime: 0,
        avgDouble: 0,
        avgTotalWages: 0,
        byTrade: [],
        byWorkSchedule: [],
        rateDistribution: [],
      };
    }

    const totalRecords = data.length;
    const uniqueTrades = new Set(data.map(d => d.ds_trade || d.ns_trade).filter(Boolean)).size;

    const validDS = data.filter(d => d.ds_straight != null);
    const avgStraight = validDS.length > 0 ? validDS.reduce((s, d) => s + (parseFloat(d.ds_straight) || 0), 0) / validDS.length : 0;
    const avgOvertime = validDS.length > 0 ? validDS.reduce((s, d) => s + (parseFloat(d.ds_overtime) || 0), 0) / validDS.length : 0;
    const avgDouble = validDS.length > 0 ? validDS.reduce((s, d) => s + (parseFloat(d.ds_double) || 0), 0) / validDS.length : 0;
    const avgTotalWages = validDS.length > 0 ? validDS.reduce((s, d) => s + (parseFloat(d.ds_total_wages) || 0), 0) / validDS.length : 0;

    // By Trade
    const tradeMap = {};
    data.forEach(d => {
      const trade = d.ds_trade || d.ns_trade || 'Unspecified';
      if (!tradeMap[trade]) {
        tradeMap[trade] = { name: trade, count: 0, straight: 0, overtime: 0, double: 0, totalWages: 0 };
      }
      tradeMap[trade].count++;
      tradeMap[trade].straight += parseFloat(d.ds_straight) || 0;
      tradeMap[trade].overtime += parseFloat(d.ds_overtime) || 0;
      tradeMap[trade].double += parseFloat(d.ds_double) || 0;
      tradeMap[trade].totalWages += parseFloat(d.ds_total_wages) || 0;
    });
    const byTrade = Object.values(tradeMap).map(t => ({
      ...t,
      avgStraight: t.count > 0 ? t.straight / t.count : 0,
      avgOvertime: t.count > 0 ? t.overtime / t.count : 0,
      avgDouble: t.count > 0 ? t.double / t.count : 0,
      avgTotalWages: t.count > 0 ? t.totalWages / t.count : 0,
    })).sort((a, b) => b.avgTotalWages - a.avgTotalWages);

    // By Work Schedule
    const schedMap = {};
    data.forEach(d => {
      const sched = d.ds_work_schedule || d.ns_work_schedule || 'Unspecified';
      if (!schedMap[sched]) {
        schedMap[sched] = { name: sched, count: 0, avgWages: 0 };
      }
      schedMap[sched].count++;
      schedMap[sched].avgWages += parseFloat(d.ds_total_wages) || 0;
    });
    const byWorkSchedule = Object.values(schedMap).map(s => ({
      ...s,
      avgWages: s.count > 0 ? s.avgWages / s.count : 0,
    })).sort((a, b) => b.count - a.count);

    // Rate distribution
    const rateDistribution = [
      { name: 'Straight', value: avgStraight },
      { name: 'Overtime', value: avgOvertime },
      { name: 'Double', value: avgDouble },
    ];

    return {
      totalRecords,
      uniqueTrades,
      avgStraight,
      avgOvertime,
      avgDouble,
      avgTotalWages,
      byTrade,
      byWorkSchedule,
      rateDistribution,
    };
  }, [data]);

  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    dark: '#1e293b',
    chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'],
  };

  const formatCurrency = (num) => '$' + num.toFixed(2);

  const MetricCard = ({ title, value, subtitle, icon, color }) => (
    <div style={{
      backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: colors.dark, margin: '0.5rem 0' }}>{value}</div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
    </div>
  );

  const BarChart = ({ data, valueKey, labelKey, title, maxBars = 10, formatValue }) => {
    const chartData = data.slice(0, maxBars);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            const value = item[valueKey] || 0;
            const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '120px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item[labelKey]}>
                  {item[labelKey]}
                </div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${percentage}%`, height: '100%',
                    backgroundColor: colors.chartColors[idx % colors.chartColors.length],
                    borderRadius: '4px', transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ width: '70px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>
                  {formatValue ? formatValue(value) : value.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'trades', label: 'By Trade', icon: '👷' },
  ];

  const renderOverview = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Total Records" value={analytics.totalRecords} subtitle={`${analytics.uniqueTrades} trades`} icon="📋" color={colors.primary} />
        <MetricCard title="Avg Straight" value={formatCurrency(analytics.avgStraight)} subtitle="Per hour" icon="💵" color={colors.success} />
        <MetricCard title="Avg Overtime" value={formatCurrency(analytics.avgOvertime)} subtitle="1.5x rate" icon="⏰" color={colors.warning} />
        <MetricCard title="Avg Double" value={formatCurrency(analytics.avgDouble)} subtitle="2x rate" icon="🔥" color={colors.danger} />
        <MetricCard title="Avg Total Wages" value={formatCurrency(analytics.avgTotalWages)} subtitle="Per record" icon="💰" color={colors.info} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byTrade} valueKey="avgTotalWages" labelKey="name" title="Avg Total Wages by Trade" formatValue={formatCurrency} />
        <BarChart data={analytics.byWorkSchedule} valueKey="count" labelKey="name" title="Records by Work Schedule" formatValue={(v) => v.toFixed(0)} />
      </div>
    </>
  );

  const renderTrades = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <BarChart data={analytics.byTrade} valueKey="avgStraight" labelKey="name" title="Avg Straight Rate by Trade" formatValue={formatCurrency} />
        <BarChart data={analytics.byTrade} valueKey="avgOvertime" labelKey="name" title="Avg Overtime Rate by Trade" formatValue={formatCurrency} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.byTrade} valueKey="avgDouble" labelKey="name" title="Avg Double Rate by Trade" formatValue={formatCurrency} />
        <BarChart data={analytics.byTrade} valueKey="count" labelKey="name" title="Records per Trade" formatValue={(v) => v.toFixed(0)} />
      </div>
    </>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem',
    }}>
      <div style={{
        backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }}>
        <div style={{
          padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.info} 100%)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>👷 Labour Rates Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Rate comparisons, trade distributions, and wage analysis</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem',
            }}>✕ Close</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setSelectedView(tab.id)} style={{
              padding: '0.75rem 1.25rem', backgroundColor: selectedView === tab.id ? colors.success : 'transparent',
              color: selectedView === tab.id ? 'white' : '#64748b', border: 'none', borderRadius: '8px 8px 0 0',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRecords === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
              <p style={{ margin: '0.5rem 0 0' }}>Add labour rates data to see analytics</p>
            </div>
          ) : (
            <>
              {selectedView === 'overview' && renderOverview()}
              {selectedView === 'trades' && renderTrades()}
            </>
          )}
        </div>

        <div style={{
          padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#94a3b8',
        }}>
          <span>{analytics.totalRecords} records | {analytics.uniqueTrades} trades</span>
          <span>Avg: {formatCurrency(analytics.avgStraight)} straight | {formatCurrency(analytics.avgOvertime)} OT | {formatCurrency(analytics.avgDouble)} double</span>
        </div>
      </div>
    </div>
  );
};

export default LabourRatesAnalyticsDashboard;

